import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ChevronDown, ChevronUp, Plus, Trash2, Edit2, Pencil } from 'lucide-react';
import AddFollowUpDialog from '@/components/AddFollowUpDialog';
import EditDecisionDialog from '@/components/EditDecisionDialog';
import EditFollowUpDialog from '@/components/EditFollowUpDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function DecisionCard({ decision, onUpdate, onDelete }) {
  const { getAuthHeaders, API } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [settingOutcome, setSettingOutcome] = useState(false);
  const [reflectionText, setReflectionText] = useState(decision.reflection || '');
  const [savingReflection, setSavingReflection] = useState(false);
  const [showReflectionInput, setShowReflectionInput] = useState(false);

  const openFollowUps = decision.follow_ups?.filter(f => f.status === 'open') || [];
  const doneFollowUps = decision.follow_ups?.filter(f => f.status === 'done') || [];
  const allFollowUpsDone = decision.follow_ups?.length > 0 && openFollowUps.length === 0;
  const canSetOutcome = allFollowUpsDone || decision.follow_ups?.length === 0;

  const formatOutcome = (outcome) => {
    const labels = {
      'as_expected': 'As expected',
      'partly_as_expected': 'Partly as expected',
      'not_as_expected': 'Not as expected',
      // Legacy values for backwards compatibility
      'good': 'As expected',
      'mixed': 'Partly as expected',
      'poor': 'Not as expected'
    };
    return labels[outcome] || outcome;
  };

  const handleToggleFollowUp = async (followUpId, currentStatus) => {
    setUpdating(true);
    const newStatus = currentStatus === 'open' ? 'done' : 'open';
    
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}/followups/${followUpId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      onUpdate(response.data);
    } catch (error) {
      console.error('Error updating follow-up:', error);
      toast.error('Failed to update follow-up');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteFollowUp = async (followUpId) => {
    try {
      const response = await axios.delete(
        `${API}/decisions/${decision.id}/followups/${followUpId}`,
        { headers: getAuthHeaders() }
      );
      onUpdate(response.data);
      toast.success('Follow-up removed');
    } catch (error) {
      console.error('Error deleting follow-up:', error);
      toast.error('Failed to delete follow-up');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/decisions/${decision.id}`, {
        headers: getAuthHeaders()
      });
      onDelete(decision.id);
    } catch (error) {
      console.error('Error deleting decision:', error);
      toast.error('Failed to delete decision');
    }
  };

  const handleSetOutcome = async (outcome) => {
    if (settingOutcome) return;
    
    // If clicking the same outcome, clear it
    const newOutcome = decision.outcome === outcome ? null : outcome;
    
    setSettingOutcome(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        { outcome: newOutcome },
        { headers: getAuthHeaders() }
      );
      onUpdate(response.data);
      if (newOutcome) {
        toast.success('Outcome recorded');
      }
    } catch (error) {
      console.error('Error setting outcome:', error);
      toast.error(error.response?.data?.detail || 'Failed to set outcome');
    } finally {
      setSettingOutcome(false);
    }
  };

  const handleSaveReflection = async () => {
    if (savingReflection) return;
    
    setSavingReflection(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        { reflection: reflectionText.trim() },
        { headers: getAuthHeaders() }
      );
      onUpdate(response.data);
      setShowReflectionInput(false);
      toast.success('Reflection saved');
    } catch (error) {
      console.error('Error saving reflection:', error);
      toast.error(error.response?.data?.detail || 'Failed to save reflection');
    } finally {
      setSavingReflection(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d');
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <div 
        className="decision-card bg-card border border-border rounded-lg p-6"
        data-testid={`decision-card-${decision.id}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="text-sm text-muted-foreground">
                {formatDate(decision.date)}
              </span>
              {decision.decision_type && (
                <span className="text-xs text-muted-foreground/70 capitalize">
                  {decision.decision_type}
                </span>
              )}
              {decision.outcome && (
                <span className="text-xs text-muted-foreground/70">
                  {formatOutcome(decision.outcome)}
                </span>
              )}
              {decision.status && decision.status !== 'active' && (
                <span className="text-xs text-muted-foreground/70 capitalize">
                  {decision.status}
                </span>
              )}
              {decision.important && (
                <span className="text-xs text-muted-foreground/70">
                  Important
                </span>
              )}
              {openFollowUps.length > 0 && (
                <span className="text-xs px-2 py-0.5 bg-accent/10 text-accent rounded">
                  {openFollowUps.length} open
                </span>
              )}
            </div>
            <h3 
              className="text-xl font-medium text-foreground mb-2 cursor-pointer hover:text-accent transition-colors"
              onClick={() => setExpanded(!expanded)}
              data-testid="decision-title"
            >
              {decision.title}
            </h3>
            <p className="text-muted-foreground leading-relaxed" data-testid="decision-context">
              {decision.context}
            </p>
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowEditDialog(true)}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              data-testid="edit-decision-button"
            >
              <Edit2 size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              data-testid="delete-decision-button"
            >
              <Trash2 size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setExpanded(!expanded)}
              className="h-8 w-8"
              data-testid="expand-decision-button"
            >
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          </div>
        </div>

        {/* Expanded Content - Follow-ups */}
        {expanded && (
          <div className="mt-6 pt-6 border-t border-border animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm tracking-wide text-muted-foreground uppercase">
                Follow-up Actions
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddFollowUp(true)}
                className="h-8 text-xs"
                data-testid="add-followup-button"
              >
                <Plus size={14} className="mr-1" />
                Add
              </Button>
            </div>

            {decision.follow_ups?.length === 0 ? (
              <p className="text-sm text-muted-foreground/70 text-center py-4">
                No follow-up actions yet.
              </p>
            ) : (
              <div className="space-y-3">
                {/* Open Follow-ups */}
                {openFollowUps.map((followUp) => (
                  <div 
                    key={followUp.id}
                    className="followup-item flex items-start gap-3 p-3 rounded-lg group"
                    data-testid={`followup-item-${followUp.id}`}
                  >
                    <Checkbox
                      checked={false}
                      onCheckedChange={() => handleToggleFollowUp(followUp.id, 'open')}
                      disabled={updating}
                      className="mt-0.5"
                      data-testid={`followup-checkbox-${followUp.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground">{followUp.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Due {formatDate(followUp.due_date)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingFollowUp(followUp)}
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        data-testid={`edit-followup-${followUp.id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteFollowUp(followUp.id)}
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        data-testid={`delete-followup-${followUp.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}

                {/* Done Follow-ups */}
                {doneFollowUps.length > 0 && (
                  <div className="pt-3 mt-3 border-t border-border/50">
                    <p className="text-xs text-muted-foreground/70 mb-2">Completed</p>
                    {doneFollowUps.map((followUp) => (
                      <div 
                        key={followUp.id}
                        className="followup-item flex items-start gap-3 p-3 rounded-lg group"
                        data-testid={`followup-item-${followUp.id}`}
                      >
                        <Checkbox
                          checked={true}
                          onCheckedChange={() => handleToggleFollowUp(followUp.id, 'done')}
                          disabled={updating}
                          className="mt-0.5"
                          data-testid={`followup-checkbox-${followUp.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-muted-foreground line-through">{followUp.description}</p>
                          <p className="text-xs text-muted-foreground/70 mt-1">
                            Due {formatDate(followUp.due_date)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingFollowUp(followUp)}
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            data-testid={`edit-followup-${followUp.id}`}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteFollowUp(followUp.id)}
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            data-testid={`delete-followup-${followUp.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Decision Outcome Section */}
            {canSetOutcome && (
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm tracking-wide text-muted-foreground uppercase mb-3">
                  Decision outcome
                </p>
                <p className="text-sm text-muted-foreground/70 mb-4">
                  How did this decision compare to your expectations?
                </p>
                <div className="flex flex-wrap gap-2" data-testid="outcome-buttons">
                  {[
                    { value: 'as_expected', label: 'As expected' },
                    { value: 'partly_as_expected', label: 'Partly as expected' },
                    { value: 'not_as_expected', label: 'Not as expected' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleSetOutcome(option.value)}
                      disabled={settingOutcome}
                      className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
                        decision.outcome === option.value
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'
                      }`}
                      data-testid={`outcome-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground/50 mt-3">
                  You can leave this unset.
                </p>
                {decision.outcome && (
                  <p className="text-xs text-muted-foreground/50 mt-1">
                    Click again to clear.
                  </p>
                )}

                {/* Reflection Section - only shows after outcome is set */}
                {decision.outcome && (
                  <div className="mt-6 pt-6 border-t border-border/50">
                    <p className="text-sm tracking-wide text-muted-foreground uppercase mb-3">
                      Reflection
                    </p>
                    
                    {decision.reflection && !showReflectionInput ? (
                      <div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          {decision.reflection}
                        </p>
                        <button
                          onClick={() => {
                            setReflectionText(decision.reflection);
                            setShowReflectionInput(true);
                          }}
                          className="text-xs text-muted-foreground/50 hover:text-muted-foreground mt-3 transition-colors"
                          data-testid="edit-reflection-btn"
                        >
                          Edit reflection
                        </button>
                      </div>
                    ) : showReflectionInput ? (
                      <div>
                        <Textarea
                          value={reflectionText}
                          onChange={(e) => setReflectionText(e.target.value.slice(0, 500))}
                          placeholder="Anything worth remembering about how this decision played out?"
                          className="min-h-[80px] resize-none text-sm"
                          data-testid="reflection-textarea"
                        />
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-xs text-muted-foreground/50">
                            {reflectionText.length}/500
                          </span>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowReflectionInput(false);
                                setReflectionText(decision.reflection || '');
                              }}
                              className="text-xs h-8"
                              data-testid="cancel-reflection-btn"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveReflection}
                              disabled={savingReflection || !reflectionText.trim()}
                              className="text-xs h-8"
                              data-testid="save-reflection-btn"
                            >
                              {savingReflection ? 'Saving...' : 'Save'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowReflectionInput(true)}
                        className="text-sm text-muted-foreground/70 hover:text-muted-foreground transition-colors"
                        data-testid="add-reflection-btn"
                      >
                        Anything worth remembering about how this decision played out?
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Show message when outcome cannot be set yet */}
            {!canSetOutcome && openFollowUps.length > 0 && (
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground/50">
                  Complete all follow-ups to record the outcome.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Follow-up Dialog */}
      <AddFollowUpDialog
        open={showAddFollowUp}
        onOpenChange={setShowAddFollowUp}
        decisionId={decision.id}
        onAdded={onUpdate}
      />

      {/* Edit Decision Dialog */}
      <EditDecisionDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        decision={decision}
        onUpdated={onUpdate}
      />

      {/* Edit Follow-up Dialog */}
      <EditFollowUpDialog
        open={!!editingFollowUp}
        onOpenChange={(open) => !open && setEditingFollowUp(null)}
        decisionId={decision.id}
        followUp={editingFollowUp}
        onUpdated={onUpdate}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Decision</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{decision.title}" and all its follow-up actions. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
